/* eslint-disable no-nested-ternary */

/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @author Satyaki Bose
 * AddEditAssignmentForm
 * Reusable Add/Edit Assignment Form component
 */


import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Paper,
  CircularProgress,
  IconButton
} from '@material-ui/core';
import PropTypes from 'prop-types';

import Utils from '../../common/utils';
import { useSyllabus } from '../../contexts/SyllabusContext';
import useAssignmentStyles from '../styles/AetAssignmentStyles';
import TreeViewCheckbox from '../common/TreeViewCheckbox';
import DateTextField from '../common/DateTextField';
import TextInputField from '../common/TextInputField';
import {
  MS_PER_DAY,
  ASSIGNMENT_TYPES,
  ASSIGNMENT_FORM_ACTIONS,
  TIMEFIELD_CONSTANTS,
  SYLLABUS_UI_CONSTANTS
} from '../../common/constants';
import ConditionalWrapper from '../common/ConditionalWrapper';
import Icons from '../icons/Icon';
import TimeTextField from '../common/TimeTextField';
import CustomScrollbar from '../CustomScrollbar/CustomScrollbar';
import ValidationUtils from '../../common/validationUtils';
import TooltipWrapper from '../tooltip/TooltipWrapper';
import AetTimezone from '../common/AetTimeZone';
import { matchTimezoneToOptions, wallClockToBrowserLocal } from '../../common/dateTimeUtils';
import FooterDeatilsCard from '../common/FooterDeatilsCard';

const FormContent = ({ formData,
  isSaveDisabled,
  handleInputChange,
  parentApp,
  tocData,
  handleChapterChange,
  chapterMcqMapping,
  handleSaveAndAdd,
  handleSaveAndClose,
  textInfo,
  assignmentConfig,
  classes,
  isEditing,
  assignmentFormActions,
  restrictAlreadyAssigned,
  renderInModal,
  addAssignmentCount,
  isValidationRequired,
  validation,
  treeViewCheckboxStyle, showAssignmentTitle,
  timeZoneOptions,
  onClose }) => {
  const cancelInsteadOfDelete = assignmentConfig?.cancelInsteadOfDelete;
  const { sections, questions } = Utils.calculateSectionsAndQuestions(formData, chapterMcqMapping, tocData);
  const coverageIds = Utils.getCoveragePageIdsForPayload(formData.chapters, tocData);

  // get all level titles and ids that are part of selection
  const allSelectedTitles = Utils.resolveDisplayTitles(coverageIds, tocData, true);

  // gives count of chapters covered, irrespective of partial or full selection
  const chaptersWithSelection = new Set(
    formData.chapters
      .map(chapter => Utils.getTopLevelChapterId(chapter, tocData))
      .filter(Boolean)
  ).size;

  // always gives full list of only root level chapter ids
  const topLevelChapterIds = new Set(
    tocData
      .filter(chapter => typeof chapter === 'object' && chapter?.id)
      .map(chapter => chapter.id)
  );
  // any displayed id that isnt part of toplevelchapters means partial selection happened
  const showSectionsSummary = allSelectedTitles.some(({ id }) => !topLevelChapterIds.has(id));
  const assignmentsCount = addAssignmentCount;
  const formTitle = isEditing
    ? textInfo.editAssignmentLabel
    : textInfo.addAssignmentLabel;
  const showTimeZone = timeZoneOptions?.length > 0;
  const [isFooterDialogOpen, setFooterDialogOpen] = useState(false);

  const openFooterDialog = () => setFooterDialogOpen(true);
  const closeFooterDialog = () => setFooterDialogOpen(false);

  const renderOverlayDiv = () => {
    if (!assignmentConfig?.disableTreeViewCheckbox) {
      return;
    }

    // eslint-disable-next-line consistent-return
    return (
      <div className={classes.overlay} />
    );
  };

  return (
    <>
      {renderInModal && (
        <Typography className={classes.title}>
          {formTitle}
        </Typography>
      )}
      {/* Due Date and Due Time Section */}
      <div
        className={
          renderInModal
            ? classes.dateTimeSectionModal
            : classes.dateTimeSection
        }
        style={{
          /* 520px: date/time (144+144+8+8) + timezone column ≥200px */
          '--form-fields-max-width': '520px',
          width: 520
        }}
      >
        {showAssignmentTitle &&
        <div className={classes.assignmentTitleField}>
          <TextInputField
            label={textInfo.assignmentTitleLabel}
            value={formData.title}
            onChange={(value) => handleInputChange('title', value)}
            placeholder={textInfo.assignmentTitlePlaceholder}
            required
            errorMessage={isValidationRequired && validation?.titleError ? validation.titleError : null}
            titleHelperText={validation.titleHelperText}
            showEditIcon={false}
            disabled={assignmentConfig?.disableAssignmentTitle}
          />
        </div>
        }
        <div className={classes.dateTimeRow}>
          <div className={classes.dateTimeField}>
            <DateTextField
              label={textInfo.dueDateLabel}
              value={formData.date}
              onChange={e => handleInputChange('date', e.target.value)}
              parentApp={parentApp}
              icons={{ calendar: <Icons name="CalendarIcon" /> }}
              required
              hasError={isValidationRequired && validation?.hasDateError}
            />
          </div>
          <div className={classes.dateTimeField}>
            <TimeTextField
              label={textInfo.dueTimeLabel}
              value={formData.time}
              onChange={(e) => handleInputChange('time', e.target.value)}
              parentApp={parentApp}
              icons={{ clock: <Icons name="ClockIcon" /> }}
            />
          </div>
          {showTimeZone && (
          <div className={classes.dateTimeFieldTimezone}>
            <AetTimezone
              label={textInfo.timeZoneLabel}
              value={formData.timeZone}
              onChange={(value) => handleInputChange('timeZone', value)}
              parentApp={parentApp}
              icons={{ timeZone: <Icons name="TimeZoneIcon" /> }}
              options={timeZoneOptions}
            />
          </div>
          )}
        </div>
      </div>

      {/* Chapter Selection Section */}
      <div className={classes.chapterSection}>
        <Typography className={classes.chapterHeader}>
          {textInfo.chaptersCoveredLabel}
        </Typography>
        <TreeViewCheckbox
          data={tocData}
          selectedItems={formData.chapters}
          onSelectionChange={handleChapterChange}
          parentApp={parentApp}
          questionCounts={chapterMcqMapping}
          headerText=""
          restrictAlreadyAssigned={restrictAlreadyAssigned}
          textInfo={textInfo}
          hasError={isValidationRequired && validation?.hasChapterError}
          style={treeViewCheckboxStyle}
          isInModal={renderInModal}
        />
        {renderOverlayDiv()}
      </div>

      {/* Summary and Action Buttons */}
      <div className={classes.summaryInfoWrapper}>
        <div className={classes.summaryInfo}>
          <Typography className={classes.summaryText}>
            {chaptersWithSelection} {textInfo?.chaptersLabel}
          </Typography>
          {showSectionsSummary && (
            <Typography className={classes.summaryText}>
              {sections} {textInfo?.sectionLabel}
            </Typography>
          )}
          <Typography className={classes.summaryQuestion}>
            {questions} {textInfo?.questionsLabel}
          </Typography>
        </div>
        {!isEditing && (
          <Typography className={classes.assignmentsCount}>
            {assignmentsCount} {textInfo?.assignmentsLabel}
          </Typography>
        )}
        {renderOverlayDiv()}
      </div>
      <div className={classes.actionSection}>

        <div className={classes.buttonGroup}>
          <TooltipWrapper
            tooltip={textInfo.selectChapterWithQuestions}
            showTooltip={!isEditing && !validation?.isValid && validation?.hasNoQuestions}
            placement="top"
          >
            <span>
              <Button
                onClick={() => {
                // Check if its editmode and has to show cancel instead of delete
                  if (isEditing && cancelInsteadOfDelete) {
                    onClose();
                  } else {
                  // Otherwise, handle Save or Delete based on whether it's editing
                    handleSaveAndClose(isEditing ? ASSIGNMENT_FORM_ACTIONS.DELETE : ASSIGNMENT_FORM_ACTIONS.SAVE_AND_CLOSE);
                  }
                }
                }
                className={classes.saveAndCloseButton}
                disabled={isEditing ? false : !validation?.isValid}
              >
                {isEditing && cancelInsteadOfDelete
                  ? assignmentFormActions.cancel
                  : (isEditing ? assignmentFormActions.delete : assignmentFormActions.saveAndClose)}
              </Button>
            </span>
          </TooltipWrapper>
          <TooltipWrapper
            tooltip={textInfo.selectChapterWithQuestions}
            showTooltip={isSaveDisabled && validation?.hasNoQuestions}
            placement="top"
          >
            <span>
              <Button
                onClick={() => handleSaveAndAdd(isEditing ? ASSIGNMENT_FORM_ACTIONS.SAVE : ASSIGNMENT_FORM_ACTIONS.SAVE_AND_ADD)}
                className={classes.saveAndAddButton}
                disabled={isSaveDisabled}
              >
                {isEditing ? assignmentFormActions.save : assignmentFormActions.saveAndAdd}
              </Button>
            </span>
          </TooltipWrapper>
        </div>
        {(textInfo?.footer && renderInModal) && (
        <>
          <div className={classes.footerWrapper}>
            <Typography className={classes.footer}>
              {textInfo.footer}
              {textInfo?.footerDialogDetails && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openFooterDialog();
                }}
              >
                <Icons name="FooterInfoIcon" style={{ fontSize: 16 }} />
              </IconButton>
              )}
            </Typography>
          </div>

          {textInfo?.footerDialogDetails && (
          <FooterDeatilsCard
            open={isFooterDialogOpen}
            onClose={closeFooterDialog}
            title={textInfo.footerDialogDetails.title}
            content={textInfo.footerDialogDetails.content}
            confirmText={
              textInfo.footerDialogDetails.confirmText || 'Confirm'
            }
            showCloseButton={
              textInfo.footerDialogDetails.showCloseButton ?? true
            }
            onConfirm={
              textInfo.footerDialogDetails.onConfirm || closeFooterDialog
            }
            customStyles={{
              '--disclaimer-max-width': '720px'
            }}
          />
          )}
        </>
        )}
      </div>
    </>
  );
};

FormContent.propTypes = {
  formData: PropTypes.object.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  parentApp: PropTypes.string.isRequired,
  assignmentConfig: PropTypes.object,
  tocData: PropTypes.array.isRequired,
  handleChapterChange: PropTypes.func.isRequired,
  chapterMcqMapping: PropTypes.object.isRequired,
  handleSaveAndAdd: PropTypes.func.isRequired,
  handleSaveAndClose: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  textInfo: PropTypes.object.isRequired,
  isEditing: PropTypes.object.isRequired,
  assignmentFormActions: PropTypes.object.isRequired,
  restrictAlreadyAssigned: PropTypes.bool,
  renderInModal: PropTypes.bool,
  addAssignmentCount: PropTypes.number,
  isSaveDisabled: PropTypes.bool,
  isValidationRequired: PropTypes.bool,
  validation: PropTypes.object,
  treeViewCheckboxStyle: PropTypes.object,
  showAssignmentTitle: PropTypes.bool,
  timeZoneOptions: PropTypes.array,
  onClose: PropTypes.func.isRequired
};

FormContent.defaultProps = {
  restrictAlreadyAssigned: true,
  assignmentConfig: {},
  renderInModal: false,
  addAssignmentCount: 0,
  isSaveDisabled: false,
  isValidationRequired: false,
  validation: null,
  treeViewCheckboxStyle: {},
  showAssignmentTitle: false,
  timeZoneOptions: []
};

export default function AddEditAssignmentForm({
  open,
  onClose,
  parentApp,
  editingAssignment,
  editIndex,
  textInfo,
  assignmentConfig,
  tocData,
  chapterMcqMapping,
  renderInModal = false,
  onSaveAndAdd,
  onSaveAndClose,
  onDelete,
  assignmentFormActions,
  restrictAlreadyAssigned,
  isLocalEditing,
  activeEditingIndex,
  addAssignmentCount,
  isValidationRequired,
  isLoading,
  excludeTitles,
  showAssignmentTitle,
  timeZoneOptions,
  titleValidationConfig
}) {
  const classes = Utils.getSyllabusStyles(
    parentApp,
    { aet: useAssignmentStyles, onereader: null },
    useAssignmentStyles(renderInModal)
  );
  const { state, actions } = useSyllabus();
  const [originalFormData, setOriginalFormData] = useState(null);

  const isEditing = editingAssignment !== null;
  // only show the title field for edit modes, not in add
  const showTitleField = Boolean(showAssignmentTitle && isEditing);

  // to show the local timezone always when form is opened
  const resolvedDefaultTimeZone = useMemo(
    () => matchTimezoneToOptions(timeZoneOptions) || timeZoneOptions?.[0]?.value,
    [timeZoneOptions]
  );

  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: TIMEFIELD_CONSTANTS.DEFAULT_FORM_TIME,
    timeZone: resolvedDefaultTimeZone,
    type: ASSIGNMENT_TYPES.HOMEWORK,
    chapters: []
  });

  /**
   * Check if form data has meaningful changes compared to original
   * @returns {boolean}
   */
  const hasChanges = () => {
    // For new assignments, always allow save if validation passes
    if (!isEditing || !originalFormData) {
      return true;
    }

    // Compare title
    if (showTitleField) {
      if (formData.title !== originalFormData.title) {
        return true;
      }
    }

    // Compare date
    if (formData.date !== originalFormData.date) {
      return true;
    }

    // Compare time
    if (formData.time !== originalFormData.time) {
      return true;
    }

    // Compare timezone
    if (formData.timeZone !== originalFormData.timeZone) {
      return true;
    }

    const currentChapterIds = new Set(
      formData.chapters.map(c => (typeof c === 'object' ? c.id : c))
    );
    const originalChapterIds = new Set(
      originalFormData.chapters.map(c => (typeof c === 'object' ? c.id : c))
    );

    if (currentChapterIds.size !== originalChapterIds.size) {
      return true;
    }

    return !Array.from(currentChapterIds).every(id => originalChapterIds.has(id));
  };

  const getExcludeTitlesForValidation = () => {
    if (!isEditing || !editingAssignment?.title) {
      return excludeTitles || [];
    }
    // should not throw error for exclusion logic while editing same
    const ownTitle = editingAssignment.title.toLowerCase();

    return excludeTitles?.filter(title => title?.toLowerCase() !== ownTitle);
  };

  const validation = ValidationUtils.validateAssignmentForm(
    formData, tocData, chapterMcqMapping, getExcludeTitlesForValidation(), showTitleField, titleValidationConfig
  );

  const isSaveDisabled = !validation?.isValid || (isEditing && !hasChanges());

  useEffect(() => {
    if (isEditing && editingAssignment) {
      // we can now save roots and subroots also into textBookCoverage
      // so we must check  this so edit mode restores the correct pages as is, and not go only by root selection logic
      const hasStoredPages = Array.isArray(editingAssignment?.textbookCoverage?.pages)
        && editingAssignment.textbookCoverage.pages.length > 0;
      const allSelectedChapters = Utils.rehydrateSelectedNodesFromCoverage(
        editingAssignment?.textbookCoverage,
        tocData,
        { preferPages: hasStoredPages || !!isLocalEditing }
      );

      // Syllabus items store wall-clock time in the instructor's selected timezone.
      // Convert to browser-local so the form matches the dashboard tabular display.
      // Dashboard/API items have no timeZone — returns null, falls back to raw values.
      const browserLocal = wallClockToBrowserLocal(
        editingAssignment.date,
        editingAssignment.time,
        editingAssignment.timeZone
      );

      const initialFormData = {
        title: editingAssignment.title || '',
        date: browserLocal?.date || editingAssignment.date || '',
        time: browserLocal?.time || editingAssignment.time || TIMEFIELD_CONSTANTS.DEFAULT_FORM_TIME,
        timeZone: resolvedDefaultTimeZone,
        type: editingAssignment.type,
        chapters: allSelectedChapters
      };

      setFormData(initialFormData);
      setOriginalFormData(initialFormData);
    } else {
      setFormData({
        title: '',
        date: state.assignmentDefaults?.date || '',
        time: state.assignmentDefaults?.time || TIMEFIELD_CONSTANTS.DEFAULT_FORM_TIME,
        timeZone: resolvedDefaultTimeZone,
        type: ASSIGNMENT_TYPES.HOMEWORK,
        chapters: []
      });
      setOriginalFormData(null);
    }
  }, [isEditing, editingAssignment, open,
    tocData, isLocalEditing, state.assignmentDefaults, timeZoneOptions, resolvedDefaultTimeZone]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      date: state.assignmentDefaults?.date || '',
      time: state.assignmentDefaults?.time || TIMEFIELD_CONSTANTS.DEFAULT_FORM_TIME,
      timeZone: resolvedDefaultTimeZone,
      type: ASSIGNMENT_TYPES.HOMEWORK,
      chapters: []
    });
  };

  const handleChapterChange = (chapter) => {
    setFormData(prev => {
      const chapterId = typeof chapter === 'object' ? chapter.id : chapter;
      const chapterTitle = typeof chapter === 'object' ? chapter.title : chapter;

      const alreadySelected = prev.chapters.some(c => {
        if (typeof c === 'object') {
          return c.id === chapterId;
        }

        return c === chapterTitle || c === chapterId;
      });

      const newChapters = alreadySelected
        ? prev.chapters.filter(c => {
          if (typeof c === 'object') {
            return c.id !== chapterId;
          }

          return c !== chapterTitle && c !== chapterId;
        })
        : [...prev.chapters, chapter];

      return {
        ...prev,
        chapters: newChapters
      };
    });
  };

  const handleSubmit = (action) => {
    if (action !== ASSIGNMENT_FORM_ACTIONS.DELETE) {
    // Validate title only when showAssignmentTitle is true
      if (showTitleField && !formData.title?.trim()) {
        console.warn('Assignment title is required');

        return;
      }
      // Always validate date and chapters
      if (!formData.date || !formData.chapters || formData.chapters.length === 0) {
        // eslint-disable-next-line no-console
        console.warn('Please fill in all required fields');

        return;
      }
    }
    // week number calculation
    const assignmentDate = new Date(formData.date);
    const startOfYear = new Date(assignmentDate.getFullYear(), 0, 1);

    const weekNumber = Math.ceil(((assignmentDate - startOfYear) / MS_PER_DAY + 1) / 7);

    // Create assignment object matching the API response structure
    const assignmentData = {
      type: ASSIGNMENT_TYPES.HOMEWORK,
      title: formData.title,
      date: formData.date,
      time: formData.time,
      timeZone: formData.timeZone,
      weekNumber,
      textbookCoverage: Utils.createTextbookCoverage(formData.chapters, tocData),
      confidence: isEditing ? (editingAssignment.confidence || 1.0) : 1.0 // Keep original confidence if editing,

    };

    if (isEditing) {
      // Update existing assignment
      actions.updateAssignment(editIndex, assignmentData);
    } else {
      // Add new assignment
      actions.addAssignment(assignmentData);
    }

    // Reset form and close
    resetForm();
    if (action === ASSIGNMENT_FORM_ACTIONS.SAVE_AND_ADD || action === ASSIGNMENT_FORM_ACTIONS.SAVE) {
      onSaveAndAdd?.(assignmentData, action);
    } else if (action === ASSIGNMENT_FORM_ACTIONS.DELETE) {
      if (onDelete) {
        // local delete from context
        onDelete(assignmentData, action);
      } else {
        // called from dashboard view, parent makes api call
        onSaveAndClose?.(assignmentData, action);
      }
    } else if (action === ASSIGNMENT_FORM_ACTIONS.SAVE_AND_CLOSE) {
      onSaveAndClose?.(assignmentData, action);
    }
  };

  return (
    <ConditionalWrapper
      condition={renderInModal}
      wrapper={Dialog}
      wrapperProps={{
        open,
        onClose,
        maxWidth: 'sm',
        fullWidth: true,
        classes: {
          paper: classes.addEditDialogPaper
        }
      }}
    >
      <ConditionalWrapper
        condition={renderInModal}
        wrapper={DialogTitle}
        wrapperProps={{
          children: isEditing ? textInfo.editAssignmentLabel : textInfo.addAssignmentLabel,
          classes: {
            root: classes.dialogTitleRoot
          }
        }}
      />
      {renderInModal &&
      <TooltipWrapper tooltip={textInfo.closeTooltipLabel || ''} placement="top">
        <div
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onClose();
            }
          }}
          className={classes.clodeIcon}
          onClick={() => onClose()}
        >
          <Icons name="CloseIcon" />
        </div>
      </TooltipWrapper>
      }
      <ConditionalWrapper
        condition={renderInModal}
        wrapper={DialogContent}
        wrapperProps={{ className: classes.dialogContent }}
      >
        <CustomScrollbar
          showScrollbar={renderInModal}
          withFlex
        >
          <ConditionalWrapper
            condition={!renderInModal}
            wrapper={Paper}
            wrapperProps={{
              className: `${classes.modernFormContainer} ${renderInModal ? classes.modalWrapper : ''}`,
              elevation: 0
            }}
          >
            <FormContent
              formData={formData}
              onClose={onClose}
              handleInputChange={handleInputChange}
              parentApp={parentApp}
              tocData={tocData}
              handleChapterChange={handleChapterChange}
              chapterMcqMapping={chapterMcqMapping}
              handleSaveAndAdd={(action) => handleSubmit(action)}
              handleSaveAndClose={(action) => handleSubmit(action)}
              textInfo={textInfo}
              assignmentConfig={assignmentConfig}
              classes={classes}
              renderInModal={renderInModal}
              isEditing={isEditing}
              assignmentFormActions={assignmentFormActions}
              restrictAlreadyAssigned={restrictAlreadyAssigned}
              activeEditingIndex={activeEditingIndex}
              editingAssignment={editingAssignment}
              addAssignmentCount={addAssignmentCount}
              isSaveDisabled={isSaveDisabled}
              isValidationRequired={isValidationRequired}
              validation={validation}
              treeViewCheckboxStyle={{
                minHeight: renderInModal
                  ? SYLLABUS_UI_CONSTANTS.CHECKBOX_CONTAINER_MIN_HEIGHT
                  : SYLLABUS_UI_CONSTANTS.CHECKBOX_CONTAINER_MAX_HEIGHT,
                maxHeight: showTitleField ? SYLLABUS_UI_CONSTANTS.CHECKBOX_CONTAINER_MAX_HEIGHT : SYLLABUS_UI_CONSTANTS.ADD_MODAL_CHECKBOX_CONTAINER_MAX_HEIGHT
              }}
              timeZoneOptions={timeZoneOptions}
              showAssignmentTitle={showTitleField}
            />
            {isLoading && (
            <div className={classes.loadingContainer}>
              <CircularProgress className={classes.loadingProgress} />
            </div>
            )}
          </ConditionalWrapper>
        </CustomScrollbar>
      </ConditionalWrapper>
    </ConditionalWrapper>
  );
}

AddEditAssignmentForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  editingAssignment: PropTypes.shape({
    title: PropTypes.string,
    date: PropTypes.string,
    time: PropTypes.string,
    timeZone: PropTypes.string,
    type: PropTypes.string,
    textbookCoverage: PropTypes.shape({
      chapters: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.shape({
          title: PropTypes.string,
          chapterId: PropTypes.string,
          chapterTitle: PropTypes.string,
          num: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        })
      ])),
      pages: PropTypes.arrayOf(PropTypes.object)
    }),
    confidence: PropTypes.number
  }),
  editIndex: PropTypes.number,
  parentApp: PropTypes.string,
  textInfo: PropTypes.object,
  assignmentConfig: PropTypes.object,
  tocData: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired
    })
  ])).isRequired,
  chapterMcqMapping: PropTypes.object,
  renderInModal: PropTypes.bool,
  onSaveAndAdd: PropTypes.func,
  onSaveAndClose: PropTypes.func,
  onDelete: PropTypes.func,
  assignmentFormActions: PropTypes.object,
  restrictAlreadyAssigned: PropTypes.bool,
  isLocalEditing: PropTypes.bool,
  activeEditingIndex: PropTypes.number,
  addAssignmentCount: PropTypes.number,
  isValidationRequired: PropTypes.bool,
  isLoading: PropTypes.bool,
  excludeTitles: PropTypes.array,
  showAssignmentTitle: PropTypes.bool,
  timeZoneOptions: PropTypes.array,
  titleValidationConfig: PropTypes.object
};

AddEditAssignmentForm.defaultProps = {
  editingAssignment: null,
  editIndex: null,
  parentApp: 'aet',
  textInfo: {},
  assignmentConfig: {},
  chapterMcqMapping: null,
  renderInModal: false,
  onSaveAndAdd: null,
  onSaveAndClose: null,
  onDelete: null,
  assignmentFormActions: {},
  restrictAlreadyAssigned: true,
  isLocalEditing: false,
  activeEditingIndex: null,
  addAssignmentCount: 0,
  isValidationRequired: false,
  isLoading: false,
  excludeTitles: [],
  showAssignmentTitle: false,
  timeZoneOptions: [],
  titleValidationConfig: {}
};

